import { lightSimulation } from '../../../lib/lightSimulation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';
import { thumbUrl } from '../../../lib/imageUrl';

type Product = {
  id: string;
  sku: string;
  brand_code: string;
  name: string;
  msrp_usd: number | null;
  cost_usd: number | null;
  status: string;
  unit: string;
  created_at: string;
  legacy_bigcommerce_id: string | null;
  legacy_lutron_line: string | null;
};

type BrandCounts = {
  total: number;
  nuled: number;
  lutron: number;
  lutron_by_line: Record<string, number>;
};

const LUTRON_LINES = [
  { slug: 'CASETA',         label: 'Caseta',     color: '#5B9BD5' },
  { slug: 'RADIORA3',       label: 'RadioRA 3',  color: '#B08D5B' },
  { slug: 'MAESTRO',        label: 'Maestro',    color: '#4A4A4A' },
  { slug: 'WALLBOX',        label: 'Wallbox',    color: '#E07B3F' },
  { slug: 'VIVE',           label: 'Vive',       color: '#3D8B7A' },
  { slug: 'HOMEWORKS QSX',  label: 'HomeWorks',  color: '#C9A961' },
  { slug: 'LUMARIS',        label: 'Lumaris',    color: '#777777' },
];

export function AdminProductos() {
  const [items, setItems] = useState<Product[]>([]);
  const [counts, setCounts] = useState<BrandCounts>({ total: 0, nuled: 0, lutron: 0, lutron_by_line: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'discontinued' | 'coming_soon'>('all');
  const [brandFilter, setBrandFilter] = useState<'all' | 'nuled' | 'lutron'>('all');
  const [lutronLineFilter, setLutronLineFilter] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [bulkField, setBulkField] = useState<'clave_sat_producto' | 'status' | 'brand_code' | 'supplier' | 'logistics_pct' | 'category'>('clave_sat_producto');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkCat, setBulkCat] = useState({ l1: '', l2: '', l3: '' });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<string | null>(null);

  const reload = () => {
    setLutronLineFilter(s => s);  // re-trigger del useEffect
    setBrandFilter(b => b);
  };

  // Cargar conteos siempre (rápido — solo COUNT)
  useEffect(() => {
    (async () => {
      const [allRes, nuledRes, lutronRes, lutronLinesRes] = await Promise.all([
        supabase.from('products').select('id', { count: 'exact', head: true }),
        supabase.from('products').select('id', { count: 'exact', head: true }).or('brand_code.eq.nuled,brand_code.is.null'),
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('brand_code', 'lutron'),
        supabase.from('products').select('legacy_lutron_line').eq('brand_code', 'lutron').limit(10000),
      ]);
      const lutronByLine: Record<string, number> = {};
      for (const r of (lutronLinesRes.data as any[] ?? [])) {
        const k = r.legacy_lutron_line ?? 'other';
        lutronByLine[k] = (lutronByLine[k] ?? 0) + 1;
      }
      setCounts({
        total: allRes.count ?? 0,
        nuled: nuledRes.count ?? 0,
        lutron: lutronRes.count ?? 0,
        lutron_by_line: lutronByLine,
      });
    })();
  }, []);

  // Proveedores (para edición masiva)
  useEffect(() => {
    supabase.from('suppliers').select('id, name').order('name').then(({ data }) => setSuppliers((data as any) ?? []));
  }, []);

  // Cargar productos según filtros (server-side, sin límite 1000)
  const loadProducts = () => {
    setLoading(true);
    let query = supabase.from('products')
      .select('id, sku, brand_code, name, msrp_usd, status, unit, created_at, legacy_bigcommerce_id, legacy_lutron_line')
      .order('created_at', { ascending: false })
      .limit(5000);

    if (brandFilter === 'nuled') {
      query = query.or('brand_code.eq.nuled,brand_code.is.null');
    } else if (brandFilter === 'lutron') {
      query = query.eq('brand_code', 'lutron');
      if (lutronLineFilter !== 'all') {
        query = query.eq('legacy_lutron_line', lutronLineFilter);
      }
    }
    if (statusFilter !== 'all') query = query.eq('status', statusFilter);

    query.then(async ({ data }) => {
      const products = ((data as Omit<Product, 'cost_usd'>[]) ?? []);
      const ids = products.map(product => product.id);
      const { data: costs } = ids.length
        ? await supabase.from('product_costs')
          .select('product_id, supplier_cost')
          .is('variant_id', null)
          .in('product_id', ids)
        : { data: [] };
      const costByProduct = new Map(
        ((costs as { product_id: string; supplier_cost: number | null }[]) ?? [])
          .map(row => [row.product_id, row.supplier_cost] as const),
      );
      setItems(products.map(product => ({
        ...product,
        cost_usd: costByProduct.get(product.id) ?? null,
      })));
      setLoading(false);
      setSelected(new Set());
    });
  };

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandFilter, statusFilter, lutronLineFilter]);

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    if (!qn) return items;
    return items.filter(p =>
      p.name.toLowerCase().includes(qn) || p.sku.toLowerCase().includes(qn)
    );
  }, [items, q]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(p => selected.has(p.id));
  function toggleOne(id: string) {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function toggleAllFiltered() {
    setSelected(allFilteredSelected ? new Set() : new Set(filtered.map(p => p.id)));
  }

  async function applyBulk() {
    const ids = Array.from(selected);
    if (!ids.length) { setBulkMsg('Selecciona al menos un producto.'); return; }
    setBulkBusy(true); setBulkMsg(null);
    const chunk = (arr: string[], n: number) => { const out: string[][] = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };
    try {
      if (bulkField === 'supplier') {
        if (!bulkValue) throw new Error('Elige un proveedor.');
        const { error } = await supabase.rpc('bulk_set_product_supplier', { p_product_ids: ids, p_supplier_id: bulkValue });
        if (error) throw error;
      } else if (bulkField === 'logistics_pct') {
        if (bulkValue === '' || isNaN(Number(bulkValue))) throw new Error('Captura un % válido.');
        const { error } = await supabase.rpc('bulk_set_product_logistics', { p_product_ids: ids, p_pct: Number(bulkValue) });
        if (error) throw error;
      } else {
        let patch: any = {};
        if (bulkField === 'clave_sat_producto') patch = { clave_sat_producto: (bulkValue || '').trim() || null };
        else if (bulkField === 'status') { if (!bulkValue) throw new Error('Elige un status.'); patch = { status: bulkValue }; }
        else if (bulkField === 'brand_code') { if (!bulkValue) throw new Error('Elige una marca.'); patch = { brand_code: bulkValue }; }
        else if (bulkField === 'category') {
          const segs = [bulkCat.l1, bulkCat.l2, bulkCat.l3].map(x => x.trim()).filter(Boolean);
          if (!segs.length) throw new Error('Captura al menos la categoría (nivel 1).');
          patch = { category_paths: segs.map((_, i) => segs.slice(0, i + 1).join('/')) };
        }
        for (const c of chunk(ids, 150)) {
          const { error } = await supabase.from('products').update(patch).in('id', c);
          if (error) throw error;
        }
      }
      setBulkMsg(`✓ Actualizados ${ids.length} producto${ids.length > 1 ? 's' : ''}.`);
      setSelected(new Set());
      loadProducts();
    } catch (e: any) {
      setBulkMsg('Error: ' + (e.message ?? e));
    }
    setBulkBusy(false);
  }

  return (
    <AdminContainer
      title="Productos"
      eyebrow={`Catálogo · ${counts.total} totales · NULED ${counts.nuled} · Lutron ${counts.lutron}`}
      action={
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '10px 16px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer', fontFamily: theme.font.sans }} onClick={() => setShowCategories(true)}>
            Gestionar categorías
          </button>
          <button style={btnPrimary} onClick={() => setShowNewForm(true)}>
            + Nuevo producto
          </button>
        </div>
      }
    >
      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          placeholder="Buscar SKU o nombre…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{
            flex: 1,
            minWidth: 260,
            padding: '10px 14px',
            border: `1px solid ${theme.border}`,
            fontSize: 14,
            outline: 'none',
            color: theme.text,
            fontFamily: theme.font.sans,
            background: theme.bg,
          }}
        />
        <Pill active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>Todos</Pill>
        <Pill active={statusFilter === 'active'} onClick={() => setStatusFilter('active')}>Activos</Pill>
        <Pill active={statusFilter === 'discontinued'} onClick={() => setStatusFilter('discontinued')}>Descontinuados</Pill>
        <Pill active={statusFilter === 'coming_soon'} onClick={() => setStatusFilter('coming_soon')}>Próximos</Pill>
        <span style={{ width: 1, alignSelf: 'stretch', background: theme.border }} />
        <Pill active={brandFilter === 'all'} onClick={() => { setBrandFilter('all'); setLutronLineFilter('all'); }}>
          Todas ({counts.total})
        </Pill>
        <Pill active={brandFilter === 'nuled'} onClick={() => { setBrandFilter('nuled'); setLutronLineFilter('all'); }}>
          NULED ({counts.nuled})
        </Pill>
        <Pill active={brandFilter === 'lutron'} onClick={() => setBrandFilter('lutron')}>
          Lutron ({counts.lutron})
        </Pill>
      </div>

      {/* Filtro secundario por línea Lutron - solo cuando Lutron está activo */}
      {brandFilter === 'lutron' && (
        <div style={{
          display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap',
          padding: '12px 14px', background: theme.bgFooter, borderLeft: `4px solid #c084fc`,
        }}>
          <span style={{
            fontSize: 11, color: theme.textMuted, alignSelf: 'center',
            textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginRight: 6,
          }}>
            Línea Lutron:
          </span>
          <LinePill active={lutronLineFilter === 'all'} color={theme.brand}
                    onClick={() => setLutronLineFilter('all')}>
            Todas ({counts.lutron})
          </LinePill>
          {LUTRON_LINES.map(line => {
            const n = counts.lutron_by_line[line.slug] ?? 0;
            if (n === 0) return null;
            return (
              <LinePill key={line.slug} active={lutronLineFilter === line.slug} color={line.color}
                        onClick={() => setLutronLineFilter(line.slug)}>
                {line.label} ({n})
              </LinePill>
            );
          })}
        </div>
      )}

      <div style={{ marginBottom: 8, fontSize: 12, color: theme.textMuted }}>
        {loading ? 'Cargando…' : `${filtered.length} de ${items.length} mostrando` + (q ? ` (filtro "${q}")` : '')}
      </div>

      {selected.size > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, padding: '12px 14px', background: theme.bgSoft, border: `1px solid ${theme.accent}` }}>
          <span style={{ fontWeight: 700, color: theme.brand, fontSize: 13 }}>{selected.size} seleccionado{selected.size > 1 ? 's' : ''}</span>
          <span style={{ color: theme.textMuted, fontSize: 12 }}>·  Cambiar</span>
          <select value={bulkField} onChange={e => { setBulkField(e.target.value as any); setBulkValue(''); setBulkMsg(null); }}
            style={{ ...inputStyle, width: 'auto', minWidth: 150 }}>
            <option value="clave_sat_producto">Clave SAT</option>
            <option value="category">Categoría</option>
            <option value="status">Status</option>
            <option value="brand_code">Marca</option>
            <option value="supplier">Proveedor</option>
            <option value="logistics_pct">% Logística</option>
          </select>
          {bulkField === 'clave_sat_producto' && (
            <input value={bulkValue} onChange={e => setBulkValue(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="25171500" inputMode="numeric" style={{ ...inputStyle, width: 140 }} />
          )}
          {bulkField === 'status' && (
            <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 160 }}>
              <option value="">— elige —</option><option value="active">Activo</option><option value="discontinued">Descontinuado</option><option value="coming_soon">Próximo</option>
            </select>
          )}
          {bulkField === 'brand_code' && (
            <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 140 }}>
              <option value="">— elige —</option><option value="nuled">NULED</option><option value="lutron">Lutron</option>
            </select>
          )}
          {bulkField === 'supplier' && (
            <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 200 }}>
              <option value="">— proveedor —</option>{suppliers.map(su => <option key={su.id} value={su.id}>{su.name}</option>)}
            </select>
          )}
          {bulkField === 'logistics_pct' && (
            <input type="number" step="0.1" value={bulkValue} onChange={e => setBulkValue(e.target.value)} placeholder="35" style={{ ...inputStyle, width: 110 }} />
          )}
          {bulkField === 'category' && (
            <>
              <input value={bulkCat.l1} onChange={e => setBulkCat({ ...bulkCat, l1: e.target.value })} placeholder="Categoría" style={{ ...inputStyle, width: 150 }} />
              <input value={bulkCat.l2} onChange={e => setBulkCat({ ...bulkCat, l2: e.target.value })} placeholder="Subcategoría" style={{ ...inputStyle, width: 150 }} />
              <input value={bulkCat.l3} onChange={e => setBulkCat({ ...bulkCat, l3: e.target.value })} placeholder="Nivel 3" style={{ ...inputStyle, width: 130 }} />
            </>
          )}
          <button onClick={applyBulk} disabled={bulkBusy} style={{ ...btnPrimary, opacity: bulkBusy ? 0.6 : 1 }}>{bulkBusy ? 'Aplicando…' : `Aplicar a ${selected.size}`}</button>
          <button onClick={() => setSelected(new Set())} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '8px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }}>Limpiar</button>
          {bulkMsg && <span style={{ fontSize: 12, fontWeight: 600, color: bulkMsg.startsWith('Error') ? '#B91C1C' : theme.accentDark }}>{bulkMsg}</span>}
        </div>
      )}

      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 900 }}>
          <thead>
            <tr style={{ background: theme.bgSoft }}>
              <th style={{ width: 34, padding: '10px 8px', textAlign: 'center' }}>
                <input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} title="Seleccionar todo el filtro" style={{ cursor: 'pointer' }} />
              </th>
              <Th>SKU</Th>
              <Th>Producto</Th>
              <Th>Marca</Th>
              <Th style={{ textAlign: 'right' }}>PVP USD</Th>
              <Th style={{ textAlign: 'right' }}>Costo USD</Th>
              <Th style={{ textAlign: 'right' }}>Margen</Th>
              <Th>Status</Th>
              <Th>BC</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 500).map(p => {
              const margin = (p.msrp_usd != null && p.cost_usd != null && p.cost_usd > 0)
                ? ((p.msrp_usd - p.cost_usd) / p.msrp_usd) * 100
                : null;
              return (
                <tr key={p.id} style={{ borderBottom: `1px solid ${theme.border}`, cursor: 'pointer' }}
                    onClick={() => setEditingId(p.id)}>
                  <td onClick={e => e.stopPropagation()} style={{ padding: '10px 8px', textAlign: 'center' }}>
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} style={{ cursor: 'pointer' }} />
                  </td>
                  <Td><span style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>{p.sku}</span></Td>
                  <Td>{p.name}</Td>
                  <Td>
                    <Tag color={p.brand_code === 'lutron' ? theme.brandTag.lutron : theme.accentDark}>
                      {p.brand_code}
                    </Tag>
                  </Td>
                  <Td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {p.msrp_usd != null ? `$${Number(p.msrp_usd).toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '—'}
                  </Td>
                  <Td style={{ textAlign: 'right', color: theme.textMuted }}>
                    {p.cost_usd != null && p.cost_usd > 0 ? `$${Number(p.cost_usd).toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '—'}
                  </Td>
                  <Td style={{ textAlign: 'right' }}>
                    {margin != null ? (
                      <span style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '2px 6px',
                        background: margin > 30 ? 'rgba(87,255,154,0.18)' : 'rgba(245,158,11,0.15)',
                        color: margin > 30 ? theme.accentDark : theme.warn,
                      }}>
                        {margin.toFixed(0)}%
                      </span>
                    ) : '—'}
                  </Td>
                  <Td>
                    <Tag color={
                      p.status === 'active' ? theme.accentDark
                      : p.status === 'discontinued' ? theme.err
                      : theme.warn
                    }>
                      {p.status}
                    </Tag>
                  </Td>
                  <Td>
                    {p.legacy_bigcommerce_id ? (
                      <span style={{ fontSize: 10, color: theme.textMuted, fontFamily: theme.font.mono }}>
                        #{p.legacy_bigcommerce_id}
                      </span>
                    ) : '—'}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {!loading && filtered.length === 0 && (
          <div style={{ padding: 48, textAlign: 'center', color: theme.textMuted }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
            <div style={{ fontFamily: theme.font.head, fontSize: 18, color: theme.brand, marginBottom: 6 }}>
              No hay productos en este filtro
            </div>
            <div style={{ fontSize: 13 }}>
              Ajusta los filtros de marca, línea o status.
            </div>
          </div>
        )}
      </div>

      {filtered.length > 200 && (
        <div style={{ marginTop: 12, fontSize: 12, color: theme.textMuted, textAlign: 'center' }}>
          Mostrando 200 de {filtered.length}. Refina con búsqueda o filtros.
        </div>
      )}

      {showNewForm && (
        <ProductModal
          mode="create"
          onClose={() => { setShowNewForm(false); loadProducts(); reload(); }}
          onSaved={() => { setShowNewForm(false); loadProducts(); reload(); }}
        />
      )}
      {editingId && (
        <ProductModal
          mode="edit"
          productId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); loadProducts(); }}
        />
      )}
      {showCategories && (
        <CategoriesModal onClose={() => setShowCategories(false)} />
      )}
    </AdminContainer>
  );
}

function CategoriesModal({ onClose }: { onClose: () => void }) {
  const [paths, setPaths] = useState<string[]>([]);
  const [l1, setL1] = useState(''); const [l2, setL2] = useState(''); const [l3, setL3] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const loadCats = () => supabase.from('product_categories').select('path').order('path')
    .then(({ data }) => setPaths(((data as any[]) ?? []).map(r => String(r.path))));
  useEffect(() => { loadCats(); }, []);

  async function add() {
    const segs = [l1, l2, l3].map(x => x.trim()).filter(Boolean);
    if (!segs.length) { setMsg('Captura al menos la categoría (nivel 1).'); return; }
    setBusy(true); setMsg(null);
    const newPaths = segs.map((_, i) => segs.slice(0, i + 1).join('/'));
    const { error } = await supabase.from('product_categories').upsert(newPaths.map(p => ({ path: p })), { onConflict: 'path', ignoreDuplicates: true });
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setL2(''); setL3(''); setMsg('✓ Agregada. Puedes seguir agregando más subcategorías bajo la misma categoría.');
    loadCats();
  }

  async function del(path: string) {
    if (!confirm(`¿Eliminar "${path}" y sus subcategorías de la lista? Los productos ya categorizados no se modifican.`)) return;
    setBusy(true); setMsg(null);
    const e1 = await supabase.from('product_categories').delete().eq('path', path);
    const e2 = await supabase.from('product_categories').delete().like('path', path + '/%');
    setBusy(false);
    if (e1.error || e2.error) { setMsg('Error: ' + (e1.error?.message || e2.error?.message)); return; }
    loadCats();
  }

  async function rename(path: string) {
    const cur = path.split('/').pop() ?? '';
    const name = prompt(`Nuevo nombre para "${cur}"\n(renombra tambien sus subcategorias y actualiza los productos):`, cur);
    if (name == null) return;
    const nn = name.trim();
    if (!nn || nn === cur) return;
    if (nn.includes('/')) { setMsg('El nombre no puede contener "/".'); return; }
    setBusy(true); setMsg(null);
    const { error } = await supabase.rpc('rename_category_path', { p_old: path, p_new_name: nn });
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setMsg('✓ Renombrada.');
    loadCats();
  }

  function addChildOf(path: string) {
    const segs = path.split('/');
    setL1(segs[0] ?? '');
    setL2(segs[1] ?? '');
    setL3('');
    setMsg(`Escribe la nueva subcategoría bajo "${path}" y presiona Agregar.`);
  }

  const lblStyle: React.CSSProperties = { fontSize: 10, color: theme.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 };

  return (
    <ProdModal onClose={onClose}>
      <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>Catálogo de categorías</div>
      <h2 style={{ fontFamily: theme.font.head, fontSize: 20, fontWeight: 400, color: theme.brand, margin: '4px 0 6px' }}>Gestionar categorías</h2>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 16, lineHeight: 1.5 }}>
        Estas son las opciones que aparecen al dar de alta o editar un producto. Agrega una categoría (nivel 1) y, si aplica, su subcategoría y nivel 3.
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
        <div><div style={lblStyle}>Categoría</div><input value={l1} onChange={e => setL1(e.target.value)} placeholder="Perfiles" style={{ ...inputStyle, width: 160 }} /></div>
        <div><div style={lblStyle}>Subcategoría</div><input value={l2} onChange={e => setL2(e.target.value)} placeholder="Aluminio" style={{ ...inputStyle, width: 160 }} /></div>
        <div><div style={lblStyle}>Nivel 3</div><input value={l3} onChange={e => setL3(e.target.value)} placeholder="Empotrado" style={{ ...inputStyle, width: 150 }} /></div>
        <button onClick={add} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>+ Agregar</button>
      </div>
      {msg && <div style={{ fontSize: 12, fontWeight: 600, color: msg.startsWith('Error') ? '#B91C1C' : theme.accentDark, marginBottom: 8 }}>{msg}</div>}

      <div style={{ maxHeight: '50vh', overflowY: 'auto', border: `1px solid ${theme.border}`, marginTop: 6 }}>
        {paths.map(p => {
          const depth = p.split('/').length - 1;
          return (
            <div key={p} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', paddingLeft: 12 + depth * 22, borderBottom: `1px solid ${theme.border}`, fontSize: 13 }}>
              <span>
                <span style={{ color: depth === 0 ? theme.brand : theme.text, fontWeight: depth === 0 ? 700 : 400 }}>{p.split('/').pop()}</span>
                <span style={{ color: theme.textMuted, fontSize: 11, marginLeft: 8 }}>{p}</span>
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                {depth < 2 && (
                  <button onClick={() => addChildOf(p)} disabled={busy} title={`Agregar subcategoría bajo ${p}`} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '2px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }}>+ sub</button>
                )}
                <button onClick={() => rename(p)} disabled={busy} title={`Renombrar ${p}`} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '2px 9px', fontSize: 12, cursor: 'pointer', fontFamily: theme.font.sans }}>✎</button>
                <button onClick={() => del(p)} disabled={busy} title="Eliminar de la lista" style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: '#B91C1C', padding: '2px 9px', fontSize: 13, cursor: 'pointer', fontFamily: theme.font.sans }}>×</button>
              </span>
            </div>
          );
        })}
        {paths.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>Sin categorías aún.</div>}
      </div>
    </ProdModal>
  );
}

// ===== Modal crear/editar producto =====
type ProductFull = {
  id?: string;
  sku: string;
  brand_code: string;
  name: string;
  short_description: string | null;
  description: string | null;
  msrp_usd: number | null;
  cost_usd: number | null;
  status: string;
  unit: string;
  units_per_box: number | null;
  weight_kg: number | null;
  stock_policy: string;
  supplier_lead_time_days: number | null;
  lutron_compatible: boolean;
  nuled_compatible: boolean;
  legacy_lutron_line: string | null;
  category_id: string | null;
  clave_sat_producto: string | null;
  tags: string[];
  specs: Record<string, string> | null;
};

const EMPTY_PRODUCT: ProductFull = {
  sku: '',
  brand_code: 'nuled',
  name: '',
  short_description: '',
  description: '',
  msrp_usd: null,
  cost_usd: null,
  status: 'active',
  unit: 'pieza',
  units_per_box: null,
  weight_kg: null,
  stock_policy: 'always_in_stock',
  supplier_lead_time_days: null,
  lutron_compatible: false,
  nuled_compatible: false,
  legacy_lutron_line: null,
  category_id: null,
  clave_sat_producto: '25171500',
  tags: [],
  specs: null,
};

// Ejes de variante CANÓNICOS (una sola clave por eje). Voltaje/potencia = productos distintos.
const VARIANT_AXES: { key: string; label: string }[] = [
  { key: 'temperatura_color', label: 'Temperatura de color' },
  { key: 'acabado', label: 'Acabado / color' },
  { key: 'angulo', label: 'Ángulo' },
  { key: 'atenuacion', label: 'Atenuación / control' },
  { key: 'dimensiones', label: 'Dimensiones / longitud' },
  { key: 'perfil', label: 'Perfil' },
];
const AXIS_LABEL: Record<string, string> = Object.fromEntries(VARIANT_AXES.map(a => [a.key, a.label]));
// Valores estándar por eje (checkboxes) — se puede agregar 'custom'
const AXIS_OPTIONS: Record<string, string[]> = {
  temperatura_color: ['2700K', '3000K', '4000K'],
  acabado: ['Blanco', 'Negro'],
  atenuacion: ['TRIAC', 'DALI', '0-10V', 'ON/OFF'],
  angulo: ['15', '24', '36', '55'],
  dimensiones: [],
  perfil: [],
};

function cartesianVariants(axes: { key: string; values: string[] }[]): Record<string, string>[] {
  const clean = axes.filter(a => a.values.length > 0);
  if (clean.length === 0) return [];
  return clean.reduce<Record<string, string>[]>((acc, ax) => {
    const out: Record<string, string>[] = [];
    for (const combo of acc) for (const val of ax.values) out.push({ ...combo, [ax.key]: val });
    return out;
  }, [{}]);
}
const numFrom = (v: unknown): number | null => { const m = String(v ?? '').match(/\d+/); return m ? Number(m[0]) : null; };
// Columnas estructuradas que lee el front, derivadas de los atributos canónicos
function typedColsFromAttrs(a: Record<string, string>) {
  return {
    color_temperature_k: numFrom(a.temperatura_color ?? a.cct),
    beam_angle_deg: numFrom(a.angulo),
    finish: (a.acabado ?? a.color ?? '').trim() || null,
    dim_method: (a.atenuacion ?? a.control ?? '').trim() || null,
  };
}

// Conserva el valor existente cuando los atributos no traen ese dato (no borrar columnas tipadas)
function mergeTyped(t: Record<string, any>, prev?: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = { ...t };
  if (prev) for (const k of Object.keys(t)) if (out[k] == null && prev[k] != null) out[k] = prev[k];
  return out;
}

// Token estandarizado por eje para armar el código de variante
const CODE_ORDER = ['temperatura_color', 'angulo', 'acabado', 'atenuacion', 'dimensiones', 'perfil'];
function abbrevToken(key: string, value: string, cust?: Record<string, string>): string {
  const v = String(value ?? '').trim();
  if (!v) return '';
  const std = AXIS_OPTIONS[key] ?? [];
  // Valor custom con código definido por el usuario
  if (!std.includes(v) && cust) { const t = (cust[`${key}::${v}`] ?? '').trim(); if (t) return t.toUpperCase().replace(/[^A-Z0-9]/g, ''); }
  if (key === 'temperatura_color') { const n = numFrom(v); return n ? String(Math.round(n / 100)) : v.replace(/[^0-9]/g, ''); } // 2700K -> 27
  if (key === 'angulo') { const n = numFrom(v); return n != null ? String(n) : v.replace(/[^0-9]/g, ''); }                    // 36° -> 36
  if (key === 'acabado') { const m: Record<string, string> = { blanco: 'B', negro: 'N', white: 'B', black: 'N' }; return m[v.toLowerCase()] ?? 'X'; }
  if (key === 'atenuacion') { const m: Record<string, string> = { 'on/off': 'NODIM', 'on-off': 'NODIM', onoff: 'NODIM', 'no dim': 'NODIM', triac: 'DIM', dali: 'DALI', '0-10v': '010V', '0-10': '010V' }; return m[v.toLowerCase()] ?? v.toUpperCase().replace(/[^A-Z0-9]/g, ''); }
  return v.toUpperCase().replace(/[^A-Z0-9]/g, ''); // dimensiones / perfil
}
function variantCode(attrs: Record<string, string>, cust?: Record<string, string>): string {
  return CODE_ORDER.filter(k => attrs[k]).map(k => abbrevToken(k, attrs[k], cust)).filter(Boolean).join('-');
}

// Mapea una clave de atributo (extracción IA / legacy) a un eje canónico, o null si no es eje
function canonAxisKey(k: string): string | null {
  const s = k.toLowerCase();
  if (/(temperatur|cct)/.test(s)) return 'temperatura_color';
  if (/(acabad|finish|colour|^color$|^color\b)/.test(s)) return 'acabado';
  if (/(angul|apertur|beam)/.test(s)) return 'angulo';
  if (/(atenu|dimm|control)/.test(s)) return 'atenuacion';
  if (/(dimension|longitud|largo|length|size|medida)/.test(s)) return 'dimensiones';
  if (/perfil/.test(s)) return 'perfil';
  return null;
}

// Normaliza un valor de eje al valor estándar (para que prenda el checkbox) o lo deja custom
function normalizeAxisVal(axis: string, raw: string): string {
  const v = String(raw ?? '').trim();
  if (!v) return v;
  if (axis === 'temperatura_color') { const n = numFrom(v); return n ? `${n}K` : v; }              // 3000 -> 3000K
  if (axis === 'angulo') { const n = numFrom(v); return n != null ? String(n) : v; }                // 15° -> 15
  if (axis === 'acabado') { const m: Record<string,string> = { blanco:'Blanco', white:'Blanco', wht:'Blanco', b:'Blanco', negro:'Negro', black:'Negro', blk:'Negro', n:'Negro' }; return m[v.toLowerCase()] ?? v; }
  if (axis === 'atenuacion') {
    const l = v.toLowerCase();
    if (/(sin atenu|no regul|no dim|nodim|on\s*[/-]?\s*off)/.test(l)) return 'ON/OFF';
    if (l.includes('triac')) return 'TRIAC';
    if (l.includes('dali')) return 'DALI';
    if (/0\s*[-–]?\s*10/.test(l)) return '0-10V';
    return v;
  }
  return v;
}

const SPEC_FIELDS: { key: string; label: string }[] = [
  { key: 'voltaje', label: 'Voltaje' },
  { key: 'potencia', label: 'Potencia (W)' },
  { key: 'cct', label: 'CCT (K)' },
  { key: 'cri', label: 'CRI' },
  { key: 'ip', label: 'IP' },
  { key: 'lumenes', label: 'Lúmenes' },
  { key: 'angulo', label: 'Ángulo (°)' },
  { key: 'dimeo', label: 'Dimeo' },
  { key: 'material', label: 'Material / Acabado' },
];

function ambiancePrompt(root: string, name: string): string {
  const r = (root || '').toLowerCase();
  const base = 'photorealistic architectural lighting photography, the exact same luminaire shown installed and turned on, preserve the product design and finish, cinematic warm lighting, high detail, no text';
  if (r.includes('estaca')) return `Night garden landscape, this LED stake/spike light uplighting a tree with warm light from below, professional landscape lighting, ${base}`;
  if (r.includes('bolardo')) return `Exterior walkway at dusk, this bollard light illuminating a garden path, modern outdoor architecture, ${base}`;
  if (r.includes('wallwash') || r.includes('wall wash') || r.includes('proyector')) return `Textured stone wall at night grazed by this fixture, dramatic wall-wash lighting, ${base}`;
  if (r.includes('downlight') || r.includes('trimless') || r.includes('serie')) return `Modern minimalist interior room at night, this recessed downlight casting a soft focused warm beam onto a wall and furniture, architectural interior photography, ${base}`;
  if (r.includes('panel')) return `Modern office ceiling with this LED panel giving clean even illumination, corporate interior, ${base}`;
  if (r.includes('manguera') || r.includes('tira') || r.includes('cinta') || r.includes('perfil')) return `Interior cove detail, this LED strip creating a warm indirect glow along a ceiling recess, luxury interior, ${base}`;
  if (r.includes('riel')) return `Contemporary retail gallery, this magnetic track light on the ceiling highlighting products on a wall, ${base}`;
  if (r.includes('exterior')) return `Exterior architectural facade at dusk with this luminaire in use, ${base}`;
  return `Interior architectural scene showcasing this ${name} luminaire installed and illuminating the space, ${base}`;
}

function ProductModal({ mode, productId, onClose, onSaved }: {
  mode: 'create' | 'edit';
  productId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [p, setP] = useState<ProductFull>(EMPTY_PRODUCT);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [specsJson, setSpecsJson] = useState('{}');
  const [simMode, setSimMode] = useState<'off'|'fixed'|'adjustable'>('off');
  const [simTilt, setSimTilt] = useState(35);
  const [tagsText, setTagsText] = useState('');
  const [catPaths, setCatPaths] = useState<string[]>([]);
  const [catL1, setCatL1] = useState('');
  const [catL2, setCatL2] = useState('');
  const [catL3, setCatL3] = useState('');
  const [specFields, setSpecFields] = useState<Record<string, string>>({});
  const [showExtract, setShowExtract] = useState(false);
  const [vrows, setVrows] = useState<{ id?: string; keep: boolean; code: string; full_sku: string; attributes: Record<string, string>; price: string; cost: string; prev?: Record<string, any> }[]>([]);
  const [axesDef, setAxesDef] = useState<{ key: string; values: string[] }[]>([]);
  const [customVal, setCustomVal] = useState<Record<string, string>>({});
  const [custTokens, setCustTokens] = useState<Record<string, string>>({});
  const [variantInv, setVariantInv] = useState<Record<string, number>>({});
  const [vsuffix, setVsuffix] = useState('');
  const [detectedCount, setDetectedCount] = useState(0);
  const [prevVariantIds, setPrevVariantIds] = useState<string[]>([]);
  const [variantInUse, setVariantInUse] = useState<Set<string>>(new Set());
  const [regenReplace, setRegenReplace] = useState(false);
  const [curMode, setCurMode] = useState(mode);
  const [curId, setCurId] = useState<string | undefined>(productId);
  const [justCreated, setJustCreated] = useState(false);
  const [ambBusy, setAmbBusy] = useState(false);
  const [ambMsg, setAmbMsg] = useState<string | null>(null);
  const [ambImg, setAmbImg] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [cost, setCost] = useState({ supplier_id: '', supplier_cost: '', cost_currency: 'USD', logistics_pct: '' });

  useEffect(() => {
    if (mode !== 'edit' || !productId) return;
    (async () => {
      const { data } = await supabase.from('products')
        .select('id, sku, brand_code, name, short_description, description, msrp_usd, status, unit, units_per_box, weight_kg, stock_policy, supplier_lead_time_days, lutron_compatible, nuled_compatible, legacy_lutron_line, category_id, category_paths, clave_sat_producto, tags, specs')
        .eq('id', productId)
        .maybeSingle();
      if (data) {
        setP({ ...data, cost_usd: null } as ProductFull);
        const sp = ((data as any).specs ?? {}) as Record<string, any>;
        const simulation = lightSimulation((data as any).sku, sp); setSimMode(simulation.mode); setSimTilt(simulation.maxTilt);
        const sf: Record<string, string> = {};
        for (const f of SPEC_FIELDS) if (sp[f.key] != null) sf[f.key] = String(sp[f.key]);
        setSpecFields(sf);
        const extras: Record<string, any> = {};
        for (const k of Object.keys(sp)) if (!SPEC_FIELDS.some(f => f.key === k)) extras[k] = sp[k];
        setSpecsJson(Object.keys(extras).length ? JSON.stringify(extras, null, 2) : '{}');
        setTagsText(((data as any).tags ?? []).join(', '));
        const paths = (((data as any).category_paths ?? []) as string[]);
        const longest = paths.reduce((a, b) => (b.split('/').length > a.split('/').length ? b : a), paths[0] ?? '');
        const segs = longest ? longest.split('/') : [];
        setCatL1(segs[0] ?? ''); setCatL2(segs[1] ?? ''); setCatL3(segs[2] ?? '');
      }
      setLoading(false);
    })();
  }, [mode, productId]);

  useEffect(() => {
    supabase.from('suppliers').select('id, name').eq('active', true).order('name')
      .then(({ data }) => setSuppliers((data as any) ?? []));
  }, []);

  useEffect(() => {
    if (mode !== 'edit' || !productId) return;
    supabase.from('product_costs')
      .select('supplier_id, supplier_cost, cost_currency, logistics_factor_pct')
      .eq('product_id', productId).is('variant_id', null).maybeSingle()
      .then(({ data }) => {
        const d = data as any;
        if (d) setCost({
          supplier_id: d.supplier_id ?? '',
          supplier_cost: d.supplier_cost != null ? String(d.supplier_cost) : '',
          cost_currency: d.cost_currency ?? 'USD',
          logistics_pct: d.logistics_factor_pct ? String(d.logistics_factor_pct) : '',
        });
      });
  }, [mode, productId]);

  useEffect(() => {
    supabase.from('product_categories').select('path').order('path')
      .then(({ data }) => setCatPaths(((data as any[]) ?? []).map(r => String(r.path))));
  }, []);

  function applyExtraction(r: any) {
    if (!r) return;
    if (r.name && !p.name.trim()) set('name', r.name);
    if (r.description && !(p.description ?? '').trim()) set('description', r.description);
    if (r.specs && typeof r.specs === 'object') {
      setSpecFields(prev => {
        const n = { ...prev };
        for (const f of SPEC_FIELDS) { const v = r.specs[f.key]; if (v != null && String(v).trim() !== '') n[f.key] = String(v); }
        return n;
      });
    }
    if (r.extras && typeof r.extras === 'object' && Object.keys(r.extras).length) {
      try { const cur = specsJson.trim() ? JSON.parse(specsJson) : {}; setSpecsJson(JSON.stringify({ ...cur, ...r.extras }, null, 2)); }
      catch { setSpecsJson(JSON.stringify(r.extras, null, 2)); }
    }
    if (Array.isArray(r.variants) && r.variants.length) {
      // Reconocer ejes canónicos de lo extraído -> precargar el constructor (el encargado deselecciona)
      const axisVals: Record<string, string[]> = {};
      for (const v of r.variants) {
        const attrs = (v.attributes ?? {}) as Record<string, any>;
        for (const [k, val] of Object.entries(attrs)) {
          const ck = canonAxisKey(k);
          if (!ck || val == null || String(val).trim() === '') continue;
          (axisVals[ck] ??= []);
          const sv = String(val).trim();
          if (!axisVals[ck].includes(sv)) axisVals[ck].push(sv);
        }
      }
      const detected = VARIANT_AXES.filter(a => axisVals[a.key]).map(a => ({ key: a.key, values: axisVals[a.key] }));
      if (detected.length) {
        setAxesDef(detected);
        setDetectedCount(r.variants.length);
      } else {
        setVrows(r.variants.map((v: any) => ({ keep: true, code: String(v.code ?? ''), full_sku: String(v.code ?? ''), attributes: (v.attributes ?? {}) as Record<string, string> })));
        setDetectedCount(r.variants.length);
      }
    }
    if (r.category) {
      const guess = String(r.category).toLowerCase();
      const root = catRoots.find(x => x.toLowerCase().includes(guess) || guess.includes(x.toLowerCase()));
      if (root) { setCatL1(root); setCatL2(''); setCatL3(''); }
    }
  }

  const catRoots = useMemo(() => Array.from(new Set(catPaths.map(pp => pp.split('/')[0]))).sort(), [catPaths]);
  const catL2opts = useMemo(() => catL1 ? Array.from(new Set(catPaths.filter(pp => { const sg = pp.split('/'); return sg[0] === catL1 && sg.length >= 2; }).map(pp => pp.split('/')[1]))).sort() : [], [catPaths, catL1]);
  const catL3opts = useMemo(() => (catL1 && catL2) ? Array.from(new Set(catPaths.filter(pp => { const sg = pp.split('/'); return sg[0] === catL1 && sg[1] === catL2 && sg.length >= 3; }).map(pp => pp.split('/')[2]))).sort() : [], [catPaths, catL1, catL2]);

  async function loadVariants() {
    if (curMode !== 'edit' || !curId) return;
    const { data } = await supabase.from('product_variants').select('id, full_sku, sku_suffix, attributes, active, msrp_usd, color_temperature_k, beam_angle_deg, finish, dim_method, position').eq('product_id', curId).order('position');
    const vs = ((data as any[]) ?? []);
    const { data: vcosts } = await supabase.from('product_costs').select('variant_id, supplier_cost').eq('product_id', curId).not('variant_id', 'is', null);
    const costMap: Record<string, string> = {};
    for (const c of ((vcosts as any[]) ?? [])) if (c.variant_id) costMap[c.variant_id] = c.supplier_cost != null ? String(c.supplier_cost) : '';
    setVrows(vs.map(v => ({ id: v.id as string, keep: true, code: (v.sku_suffix ?? '') as string, full_sku: (v.full_sku ?? '') as string, attributes: (v.attributes ?? {}) as Record<string, string>, price: v.msrp_usd != null ? String(v.msrp_usd) : '', cost: costMap[v.id] ?? '', prev: { color_temperature_k: v.color_temperature_k, beam_angle_deg: v.beam_angle_deg, finish: v.finish, dim_method: v.dim_method, position: v.position, active: v.active } })));
    // Reconstruir el constructor de ejes desde las variantes existentes (modo edición) → poder Regenerar combinaciones + SKU
    const axisVals: Record<string, string[]> = {};
    for (const v of vs) {
      const attrs = (v.attributes ?? {}) as Record<string, any>;
      for (const [k, val] of Object.entries(attrs)) {
        const ck = canonAxisKey(k);
        if (!ck || val == null || String(val).trim() === '') continue;
        const sv = normalizeAxisVal(ck, String(val));
        (axisVals[ck] ??= []);
        if (!axisVals[ck].includes(sv)) axisVals[ck].push(sv);
      }
    }
    const detected = VARIANT_AXES.filter(a => axisVals[a.key]).map(a => ({ key: a.key, values: axisVals[a.key] }));
    setAxesDef(detected);
    setPrevVariantIds(vs.map(v => v.id as string));
    const ids = vs.map(v => v.id);
    setRegenReplace(false);
    if (ids.length) {
      const { data: inv } = await supabase.from('inventory').select('variant_id, on_hand').in('variant_id', ids);
      const m: Record<string, number> = {};
      for (const r of ((inv as any[]) ?? [])) if (r.variant_id) m[r.variant_id] = (m[r.variant_id] ?? 0) + Number(r.on_hand ?? 0);
      setVariantInv(m);
      // Variantes "en uso" (con inventario o en pedidos): no se pueden reemplazar sin decisión de Elias
      const { data: oi } = await supabase.from('order_items').select('variant_id').in('variant_id', ids);
      const used = new Set<string>();
      for (const [vid, qty] of Object.entries(m)) if (Number(qty) !== 0) used.add(vid);
      for (const r of ((oi as any[]) ?? [])) if (r.variant_id) used.add(r.variant_id as string);
      const { data: rec } = await supabase.from('variant_inventory_recipes').select('sellable_variant_id').in('sellable_variant_id', ids);
      for (const r of ((rec as any[]) ?? [])) if (r.sellable_variant_id) used.add(r.sellable_variant_id as string);
      setVariantInUse(used);
    } else { setVariantInv({}); setVariantInUse(new Set()); }
  }
  useEffect(() => { loadVariants(); /* eslint-disable-next-line */ }, [curMode, curId]);

  const varyKeys = useMemo(() => { const st = new Set<string>(); for (const r of vrows) for (const k of Object.keys(r.attributes || {})) st.add(k); return Array.from(st); }, [vrows]);
  const fullSkuOf = (code: string) => [p.sku.trim(), (code || '').trim(), vsuffix.trim()].filter(Boolean).join('-');

  async function genAmbiance() {
    if (!curId) return;
    setAmbBusy(true); setAmbMsg(null); setAmbImg(null);
    const { data: pd } = await supabase.from('products').select('images').eq('id', curId).maybeSingle();
    const arr = Array.isArray((pd as any)?.images) ? (pd as any).images : [];
    const hero = typeof arr[0] === 'string' ? arr[0] : arr[0]?.url;
    if (!hero) { setAmbBusy(false); setAmbMsg('Sube una foto del producto primero (sección Fotos).'); return; }
    const prompt = ambiancePrompt(catL1, p.name);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ job_type: 'ambiance', prompt, input_refs: [hero], params: { aspect_ratio: '16:9', resolution: '1080p' }, target_kind: 'product', target_id: curId, apply_to_kind: 'product_image' }),
      });
      const data = await res.json();
      if (!data.ok) { setAmbBusy(false); setAmbMsg('✗ ' + (data.error || 'error')); return; }
      const jobId = data.job_id;
      for (let i = 0; i < 40; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: job } = await supabase.from('ai_jobs').select('status, output_urls, preview_url, error').eq('id', jobId).maybeSingle();
        const j = job as any;
        if (j?.status === 'completed') {
          const url = (j.output_urls && j.output_urls[0]) || j.preview_url;
          setAmbImg(url); setAmbBusy(false);
          setAmbMsg(data.stub ? '✓ Generado (modo prueba — configura Higgsfield para imágenes reales)' : '✓ Ambientación generada');
          if (url) {
            const cur = arr;
            await supabase.from('products').update({ images: [...cur, { url, sort_order: cur.length, is_thumbnail: false, kind: 'ambiance' }] }).eq('id', curId);
          }
          return;
        }
        if (j?.status === 'failed') { setAmbBusy(false); setAmbMsg('✗ ' + (j.error || 'falló la generación')); return; }
      }
      setAmbBusy(false); setAmbMsg('⏳ Sigue generando en segundo plano; revísalo en Contenido AI.');
    } catch (e: any) { setAmbBusy(false); setAmbMsg('✗ ' + (e.message ?? 'error')); }
  }

  const set = (k: keyof ProductFull, v: any) => setP(prev => ({ ...prev, [k]: v }));

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      let extras: any = {};
      try { extras = specsJson.trim() ? JSON.parse(specsJson) : {}; }
      catch { throw new Error('Specs JSON inválido — corrige el formato.'); }
      if(simMode === 'adjustable' && (!Number.isFinite(simTilt) || simTilt < 0 || simTilt > 90)) throw new Error('El giro máximo debe estar entre 0 y 90 grados.');
      const specsObj: any = { ...extras, _light_simulation: JSON.stringify({mode:simMode,maxTilt:simMode === 'adjustable' ? simTilt : 0}) };
      for (const f of SPEC_FIELDS) if ((specFields[f.key] ?? '') !== '') specsObj[f.key] = specFields[f.key];
      const specs = Object.keys(specsObj).length ? specsObj : null;

      const tags = tagsText.split(',').map(t => t.trim()).filter(Boolean);
      const segs = [catL1, catL2, catL3].filter(Boolean);
      const category_paths = segs.map((_, i) => segs.slice(0, i + 1).join('/'));

      const payload: any = {
        sku: p.sku.trim().toUpperCase(),
        brand_code: p.brand_code,
        name: p.name.trim(),
        short_description: p.short_description || null,
        description: p.description || null,
        msrp_usd: p.msrp_usd,
        status: p.status,
        unit: p.unit || 'pieza',
        units_per_box: p.units_per_box,
        weight_kg: p.weight_kg,
        stock_policy: p.stock_policy,
        supplier_lead_time_days: p.supplier_lead_time_days,
        lutron_compatible: p.lutron_compatible,
        nuled_compatible: p.nuled_compatible,
        legacy_lutron_line: p.brand_code === 'lutron' ? p.legacy_lutron_line : null,
        category_paths: category_paths.length ? category_paths : null,
        clave_sat_producto: (p.clave_sat_producto || '').trim() || null,
        tags,
        specs,
      };

      let pid = productId;
      const wasCreate = curMode === 'create';
      if (wasCreate) {
        const { data, error } = await supabase.from('products').insert(payload).select('id').single();
        if (error) throw error;
        pid = (data as any).id;
      } else {
        const { error } = await supabase.from('products').update(payload).eq('id', curId!);
        if (error) throw error;
        pid = curId!;
      }
      // Costo de proveedor (privado, product_costs)
      if (pid && (cost.supplier_id || cost.supplier_cost !== '' || cost.logistics_pct !== '')) {
        const { error: ce } = await supabase.rpc('upsert_product_cost', {
          p_product_id: pid, p_variant_id: null,
          p_supplier_cost: cost.supplier_cost === '' ? null : Number(cost.supplier_cost),
          p_currency: cost.cost_currency,
          p_logistics_pct: cost.logistics_pct === '' ? 0 : Number(cost.logistics_pct),
          p_notes: null, p_supplier_id: cost.supplier_id || null,
        });
        if (ce) throw ce;
      }
      // Variantes (inline)
      const keepRows = vrows.filter(v => v.keep);
      if (keepRows.length) {
        const vpayload = keepRows.map((v, i) => ({
          product_id: pid,
          sku_suffix: (v.code && v.code.trim()) ? v.code.trim() : `v${i + 1}`,
          full_sku: (v.full_sku && v.full_sku.trim()) ? v.full_sku.trim() : (fullSkuOf(v.code) || null),
          attributes: v.attributes,
          ...mergeTyped(typedColsFromAttrs(v.attributes), v.prev),  // llena columnas tipadas sin borrar las existentes
          msrp_usd: (v.price && v.price.trim()) ? Number(v.price) : (p.msrp_usd ?? null),
          active: v.prev?.active ?? true, position: v.prev?.position ?? i + 1,
        }));
        const { data: savedVars, error: ve } = await supabase.from('product_variants').upsert(vpayload, { onConflict: 'product_id,sku_suffix' }).select('id, sku_suffix');
        if (ve) throw ve;
        // Costo de proveedor POR VARIANTE (varía por longitud/atenuación)
        const idBySuffix: Record<string, string> = {};
        for (const sv of ((savedVars as any[]) ?? [])) idBySuffix[sv.sku_suffix] = sv.id;
        for (let i = 0; i < keepRows.length; i++) {
          const v = keepRows[i];
          const suf = (v.code && v.code.trim()) ? v.code.trim() : `v${i + 1}`;
          const vid = idBySuffix[suf];
          if (vid && v.cost && v.cost.trim() !== '') {
            await supabase.rpc('upsert_product_cost', {
              p_product_id: pid, p_variant_id: vid,
              p_supplier_cost: Number(v.cost),
              p_currency: cost.cost_currency,
              p_logistics_pct: cost.logistics_pct === '' ? 0 : Number(cost.logistics_pct),
              p_notes: null, p_supplier_id: cost.supplier_id || null,
            });
          }
        }
      }
      const keptIds = new Set(vrows.filter(v => v.id && v.keep).map(v => v.id as string));
      const blocked = vrows.filter(v => v.id && !v.keep && variantInUse.has(v.id as string));
      if (blocked.length) throw new Error(`No se pueden eliminar ${blocked.length} variante(s) con inventario, pedidos o receta de componentes: ${blocked.map(b => b.full_sku || b.code).join(', ')}. Desactívalas en lugar de borrarlas.`);
      const delIds = vrows.filter(v => v.id && !v.keep).map(v => v.id) as string[];
      // Al regenerar combinaciones en edición: borrar las variantes viejas (planas) que ya no quedan (garantizado sin inventario/pedidos por el guard del botón Generar)
      if (regenReplace) for (const pid0 of prevVariantIds) if (!keptIds.has(pid0) && !delIds.includes(pid0) && !variantInUse.has(pid0)) delIds.push(pid0);
      if (delIds.length) await supabase.from('product_variants').delete().in('id', delIds);
      if (wasCreate) {
        // No cerramos: pasamos a modo edicion para generar variantes de una vez.
        setCurId(pid); setCurMode('edit'); setJustCreated(true); setSaving(false);
        return;
      }
      onSaved();
    } catch (e: any) {
      setErr(e.message ?? 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <ProdModal onClose={onClose}>
        <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>Cargando…</div>
      </ProdModal>
    );
  }

  return (
    <ProdModal onClose={onClose}>
      <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>
        {curMode === 'create' ? 'Nuevo producto' : 'Editar producto'}
      </div>
      <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 20px' }}>
        {curMode === 'create' ? 'Agregar al catálogo' : (p.sku || 'Producto')}
      </h2>

      {justCreated && (
        <div style={{ padding: 12, marginBottom: 14, background: 'rgba(21,128,61,0.08)', border: '1px solid #15803D', color: '#15803D', fontSize: 13 }}>
          ✓ Producto creado con sus variantes. Agrega las fotos abajo.
        </div>
      )}
      {showExtract && <ExtractModal onClose={() => setShowExtract(false)} onApply={applyExtraction} />}
      <form onSubmit={save}>
        <div style={{ marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setShowExtract(true)} style={{ background: theme.accent, color: theme.brand, border: 'none', padding: '9px 16px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', cursor: 'pointer', fontFamily: theme.font.sans }}>
            📄 Extraer de ficha del proveedor (IA)
          </button>
          {curMode === 'edit' && curId && p.brand_code === 'nuled' && (
            <button type="button" onClick={genAmbiance} disabled={ambBusy} style={{ background: 'transparent', border: `1px solid ${theme.accent}`, color: theme.brand, padding: '9px 16px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', cursor: ambBusy ? 'wait' : 'pointer', fontFamily: theme.font.sans, opacity: ambBusy ? 0.6 : 1 }}>
              {ambBusy ? '✨ Generando ambientación…' : '✨ Ambientación (IA)'}
            </button>
          )}
        </div>
        {(ambMsg || ambImg) && (
          <div style={{ marginBottom: 14, padding: 10, background: theme.bgSoft, border: `1px solid ${theme.border}`, fontSize: 12, color: theme.brand }}>
            {ambMsg}
            {ambImg && <div style={{ marginTop: 8 }}><img src={ambImg} alt="Ambientación" style={{ maxWidth: '100%', width: 420, border: `1px solid ${theme.border}` }} /></div>}
          </div>
        )}
        <PRow>
          <PField label="SKU general / modelo *">
            <input required value={p.sku} onChange={e => set('sku', e.target.value.toUpperCase())} placeholder="FP127" style={inputStyle} />
            <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 3 }}>Es el prefijo de las variantes; el SKU de cada variante se arma con esto + el código de la variante.</div>
          </PField>
          <PField label="Marca">
            <select value={p.brand_code} onChange={e => set('brand_code', e.target.value)} style={inputStyle}>
              <option value="nuled">NULED</option>
              <option value="lutron">Lutron</option>
              <option value="other">Otro</option>
            </select>
          </PField>
        </PRow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <PField label="Categoría">
            <select value={catL1} onChange={e => { setCatL1(e.target.value); setCatL2(''); setCatL3(''); }} style={inputStyle}>
              <option value="">— categoría —</option>
              {catRoots.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </PField>
          <PField label="Subcategoría">
            <select value={catL2} onChange={e => { setCatL2(e.target.value); setCatL3(''); }} style={inputStyle} disabled={!catL1}>
              <option value="">— (opcional) —</option>
              {catL2opts.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </PField>
          <PField label="Nivel 3">
            <select value={catL3} onChange={e => setCatL3(e.target.value)} style={inputStyle} disabled={!catL2}>
              <option value="">— (opcional) —</option>
              {catL3opts.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </PField>
        </div>
        <PField label="Nombre *">
          <input required value={p.name} onChange={e => set('name', e.target.value)} style={inputStyle} />
        </PField>
        <PField label="Descripción corta">
          <input value={p.short_description ?? ''} onChange={e => set('short_description', e.target.value)} style={inputStyle} placeholder="2-3 líneas" />
        </PField>
        <PField label="Descripción completa">
          <textarea value={p.description ?? ''} onChange={e => set('description', e.target.value)} style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} />
        </PField>

        <PRow>
          <PField label="Status">
            <select value={p.status} onChange={e => set('status', e.target.value)} style={inputStyle}>
              <option value="active">Activo</option>
              <option value="coming_soon">Próximamente</option>
              <option value="discontinued">Descontinuado</option>
              <option value="out_of_stock">Agotado</option>
            </select>
          </PField>
          <PField label="Política stock">
            <select value={p.stock_policy} onChange={e => set('stock_policy', e.target.value)} style={inputStyle}>
              <option value="always_in_stock">Siempre disponible</option>
              <option value="made_to_order">Bajo pedido</option>
              <option value="hybrid">Híbrido</option>
              <option value="discontinued">Descontinuado</option>
            </select>
          </PField>
        </PRow>

        <PRow>
          <PField label="PVP USD">
            <input type="number" step="0.01" value={p.msrp_usd ?? ''} onChange={e => set('msrp_usd', e.target.value ? Number(e.target.value) : null)} style={inputStyle} />
          </PField>
          <PField label="Clave SAT (producto/servicio)">
            <input value={p.clave_sat_producto ?? ''} onChange={e => set('clave_sat_producto', e.target.value.replace(/\D/g, '').slice(0, 8))} style={inputStyle} placeholder="25171500" inputMode="numeric" />
            <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 3 }}>Va automático al CFDI. Default 25171500 (iluminación LED).</div>
          </PField>
        </PRow>

        <div style={{ margin: '6px 0 6px', fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
          🔒 Costo de proveedor (privado — alimenta compras y P&L)
        </div>
        <PRow>
          <PField label="Proveedor">
            <select value={cost.supplier_id} onChange={e => setCost({ ...cost, supplier_id: e.target.value })} style={inputStyle}>
              <option value="">— selecciona —</option>
              {suppliers.map(su => <option key={su.id} value={su.id}>{su.name}</option>)}
            </select>
          </PField>
          <PField label="Costo proveedor">
            <input type="number" step="0.01" value={cost.supplier_cost} onChange={e => setCost({ ...cost, supplier_cost: e.target.value })} style={inputStyle} />
          </PField>
          <PField label="Moneda">
            <select value={cost.cost_currency} onChange={e => setCost({ ...cost, cost_currency: e.target.value })} style={inputStyle}>
              <option value="USD">USD</option><option value="MXN">MXN</option>
            </select>
          </PField>
          <PField label="% Logística">
            <input type="number" step="0.1" value={cost.logistics_pct} onChange={e => setCost({ ...cost, logistics_pct: e.target.value })} style={inputStyle} />
          </PField>
        </PRow>

        <PRow>
          <PField label="Unidad">
            <input value={p.unit} onChange={e => set('unit', e.target.value)} style={inputStyle} placeholder="pieza, metro, kit…" />
          </PField>
          <PField label="Unidades por caja">
            <input type="number" value={p.units_per_box ?? ''} onChange={e => set('units_per_box', e.target.value ? Number(e.target.value) : null)} style={inputStyle} />
          </PField>
          <PField label="Peso kg">
            <input type="number" step="0.01" value={p.weight_kg ?? ''} onChange={e => set('weight_kg', e.target.value ? Number(e.target.value) : null)} style={inputStyle} />
          </PField>
        </PRow>

        <PRow>
          <PField label="Lead time días (proveedor)">
            <input type="number" value={p.supplier_lead_time_days ?? ''} onChange={e => set('supplier_lead_time_days', e.target.value ? Number(e.target.value) : null)} style={inputStyle} />
          </PField>
          {p.brand_code === 'lutron' && (
            <PField label="Línea Lutron">
              <select value={p.legacy_lutron_line ?? ''} onChange={e => set('legacy_lutron_line', e.target.value || null)} style={inputStyle}>
                <option value="">— sin asignar —</option>
                {LUTRON_LINES.map(l => <option key={l.slug} value={l.slug}>{l.label}</option>)}
              </select>
            </PField>
          )}
        </PRow>

        <PRow>
          <PField label="Compat Lutron">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.brand }}>
              <input type="checkbox" checked={p.lutron_compatible} onChange={e => set('lutron_compatible', e.target.checked)} />
              Compatible con sistemas Lutron
            </label>
          </PField>
          <PField label="Compat NULED">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.brand }}>
              <input type="checkbox" checked={p.nuled_compatible} onChange={e => set('nuled_compatible', e.target.checked)} />
              Compatible con luminarias NULED
            </label>
          </PField>
        </PRow>

        <PField label="Tags (separadas por coma)">
          <input value={tagsText} onChange={e => setTagsText(e.target.value)} style={inputStyle} placeholder="Bolardo, IP65, 24V…" />
        </PField>

        <div style={{ margin: '10px 0 6px', fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Características (specs)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 8 }}>
          {SPEC_FIELDS.map(f => (
            <PField key={f.key} label={f.label}>
              <input value={specFields[f.key] ?? ''} onChange={e => setSpecFields(prev => ({ ...prev, [f.key]: e.target.value }))} style={inputStyle} />
            </PField>
          ))}
        </div>

        <fieldset style={{border:'1px solid #ccd8d0',padding:16,margin:'16px 0'}}><legend>Simulación de luz</legend>
          <p style={{fontSize:13}}>Habilita solo ópticas de haz cónico revisadas. No usar en perfiles, rieles, accesorios, lineales ni wallwashers.</p>
          <label>Tipo de simulación <select value={simMode} onChange={e=>setSimMode(e.target.value as 'off'|'fixed'|'adjustable')}><option value="off">Desactivado / pendiente de revisión</option><option value="fixed">Haz cónico fijo</option><option value="adjustable">Haz cónico dirigible</option></select></label>
          {simMode === 'adjustable' && <label style={{display:'block',marginTop:12}}>Giro máximo verificado (grados) <input type="number" min={0} max={90} value={simTilt} onChange={e=>setSimTilt(Number(e.target.value))}/></label>}
          <p style={{fontSize:12}}>El simulador se oculta si la variante no tiene una apertura y temperatura válidas. Guarda los cambios para aplicar esta configuración.</p>
        </fieldset>
        <PField label="Otras specs (JSON · avanzado)">
          <textarea
            value={specsJson}
            onChange={e => setSpecsJson(e.target.value)}
            style={{ ...inputStyle, minHeight: 100, fontFamily: theme.font.mono, fontSize: 11, resize: 'vertical' }}
            placeholder='{"voltaje":"127V","ip":"65","watts":"10W","cct":"3000K"}'
          />
        </PField>

        {/* Variantes (inline, visibles desde el inicio) */}
        <div style={{ marginTop: 16, marginBottom: 6, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 8 }}>
            Variantes {detectedCount > 0 ? `(${detectedCount} detectadas de la ficha)` : `(${vrows.filter(v => v.keep).length})`}
          </div>
          <div style={{ border: `1px solid ${theme.accent}`, background: theme.accentSoft, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: theme.brand, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Ejes de variante · genera todas las combinaciones automáticamente
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {VARIANT_AXES.filter(a => !axesDef.some(x => x.key === a.key)).map(a => (
                <button key={a.key} type="button" onClick={() => setAxesDef(d => [...d, { key: a.key, values: [] }])}
                  style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.brand, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font.sans }}>
                  + {a.label}
                </button>
              ))}
            </div>
            {axesDef.length === 0 && (
              <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 8 }}>
                Elige uno o más ejes (ej. Temperatura de color + Acabado), captura sus valores por coma y presiona <b>Generar</b>. Se crean todas las combinaciones. (Voltaje/potencia = productos distintos, no variantes.)
              </div>
            )}
          </div>

          {axesDef.length > 0 && (
            <div style={{ border: `1px solid ${theme.border}`, padding: 12, marginBottom: 12, background: theme.bgSoft }}>
              <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Ejes y valores (marca los estándar o agrega custom)</div>
              {curMode === 'edit' && (
                <div style={{ fontSize: 11, color: theme.brand, marginBottom: 10, padding: 8, background: theme.accentSoft, border: `1px solid ${theme.accent}` }}>
                  Detecté los ejes de las variantes existentes. Ajústalos y presiona <b>Generar</b> para reconstruir las combinaciones correctas + SKU (reemplaza las variantes planas viejas).
                  {variantInUse.size > 0 && <> <b>{variantInUse.size} variante(s) tienen inventario o pedidos</b> — esas no se reemplazan; ajústalas a mano en la tabla.</>}
                </div>
              )}
              {axesDef.map((ax, i) => {
                const std = AXIS_OPTIONS[ax.key] ?? [];
                const custom = ax.values.filter(v => !std.includes(v));
                const toggle = (val: string) => setAxesDef(a => a.map((x, j) => j === i ? { ...x, values: x.values.includes(val) ? x.values.filter(v => v !== val) : [...x.values, val] } : x));
                const addCustom = () => { const v = (customVal[ax.key] ?? '').trim(); if (!v) return; setAxesDef(a => a.map((x, j) => j === i ? (x.values.includes(v) ? x : { ...x, values: [...x.values, v] }) : x)); setCustomVal(c => ({ ...c, [ax.key]: '' })); };
                return (
                  <div key={ax.key} style={{ marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${theme.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: theme.brand, fontWeight: 700 }}>{AXIS_LABEL[ax.key] ?? ax.key}</span>
                      <button type="button" onClick={() => setAxesDef(a => a.filter((_, j) => j !== i))} style={{ background: 'transparent', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: 15 }}>quitar eje ×</button>
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                      {std.map(opt => (
                        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: theme.text, cursor: 'pointer' }}>
                          <input type="checkbox" checked={ax.values.includes(opt)} onChange={() => toggle(opt)} />{opt}
                        </label>
                      ))}
                      {custom.map(v => (
                        <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, background: theme.accentSoft, color: theme.brand, padding: '3px 6px', fontWeight: 600 }}>
                          {v}
                          <input value={custTokens[`${ax.key}::${v}`] ?? ''} onChange={e => setCustTokens(c => ({ ...c, [`${ax.key}::${v}`]: e.target.value }))}
                            placeholder="cód" title="Código para el SKU" style={{ width: 48, padding: '2px 5px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.brand, fontSize: 11, fontFamily: theme.font.mono, textTransform: 'uppercase' }} />
                          <button type="button" onClick={() => toggle(v)} style={{ background: 'transparent', border: 'none', color: theme.brand, cursor: 'pointer', fontSize: 13, padding: 0 }}>×</button>
                        </span>
                      ))}
                      <input value={customVal[ax.key] ?? ''} onChange={e => setCustomVal(c => ({ ...c, [ax.key]: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
                        placeholder="+ custom" style={{ width: 110, padding: '5px 8px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 12, fontFamily: theme.font.sans }} />
                      <button type="button" onClick={addCustom} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '5px 9px', fontSize: 12, cursor: 'pointer', fontFamily: theme.font.sans }}>Agregar</button>
                    </div>
                  </div>
                );
              })}
              {(() => {
                const n = cartesianVariants(axesDef).length;
                return (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 10, color: theme.textMuted, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Prefijo = SKU del producto</div>
                      <div style={{ padding: '7px 10px', border: `1px solid ${theme.border}`, background: theme.bgSoft, color: theme.brand, fontSize: 13, fontFamily: theme.font.mono, minWidth: 120 }}>{p.sku.trim() || '— pon el SKU arriba —'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: theme.textMuted, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Sufijo (opcional)</div>
                      <input value={vsuffix} onChange={e => setVsuffix(e.target.value)} placeholder="IP20" style={{ width: 110, padding: '7px 10px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 13, fontFamily: theme.font.sans }} />
                    </div>
                    <button type="button" disabled={n === 0} onClick={() => {
                      if (curMode === 'edit' && variantInUse.size > 0) {
                        setErr(`No se puede regenerar: ${variantInUse.size} variante(s) actual(es) tienen inventario, pedidos o receta de componentes. Ajusta esas manualmente en la tabla.`);
                        return;
                      }
                      const combos = cartesianVariants(axesDef);
                      const pfx = p.sku.trim(); const sfx = vsuffix.trim();
                      if (curMode === 'edit') setRegenReplace(true);
                      setVrows(combos.map((attrs, i) => {
                        const code = variantCode(attrs, custTokens) || `v${i + 1}`;
                        return {
                          keep: true,
                          code,
                          full_sku: [pfx, code, sfx].filter(Boolean).join('-'),
                          attributes: attrs,
                          price: p.msrp_usd != null ? String(p.msrp_usd) : '',
                          cost: cost.supplier_cost || '',
                        };
                      }));
                      const valsOf = (k: string) => (axesDef.find(a => a.key === k)?.values ?? []).join(' / ');
                      setSpecFields(sf => ({ ...sf, cct: valsOf('temperatura_color') || sf.cct, angulo: valsOf('angulo') || sf.angulo, material: valsOf('acabado') || sf.material, dimeo: valsOf('atenuacion') || sf.dimeo }));
                    }} style={{ background: theme.accent, color: theme.brand, border: 'none', padding: '9px 16px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, cursor: n === 0 ? 'not-allowed' : 'pointer', opacity: n === 0 ? 0.5 : 1, fontFamily: theme.font.sans }}>
                      Generar {n} variante{n === 1 ? '' : 's'} + SKU
                    </button>
                  </div>
                );
              })()}
              <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 6 }}>Genera todas las combinaciones. Puedes editar/quitar renglones abajo y poner los SKU con el generador.</div>
            </div>
          )}

          {vrows.length === 0 ? (
            <div style={{ fontSize: 12, color: theme.textMuted, padding: '4px 0' }}>Sin variantes. Agrega ejes arriba (+ Eje de variante) y presiona Generar, o agrega manual. (Voltaje/potencia = productos distintos, no variantes.)</div>
          ) : (
            <div style={{ border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: theme.bgSoft }}>
                  <th style={{ padding: '6px 8px', width: 34 }}></th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase' }}>Código</th>
                  {varyKeys.map(k => <th key={k} style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase' }}>{k}</th>)}
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase' }}>SKU modelo</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase' }}>Precio USD</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase' }}>🔒 Costo</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase' }}>Inv.</th>
                  <th style={{ padding: '6px 8px' }}></th>
                </tr></thead>
                <tbody>
                  {vrows.map((v, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${theme.border}`, opacity: v.keep ? 1 : 0.45 }}>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <input type="checkbox" checked={v.keep} onChange={e => setVrows(vs => vs.map((x, j) => j === i ? { ...x, keep: e.target.checked } : x))} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={v.code} onChange={e => setVrows(vs => vs.map((x, j) => j === i ? { ...x, code: e.target.value } : x))} style={{ width: 72, padding: '3px 6px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 12, fontFamily: theme.font.mono }} />
                      </td>
                      {varyKeys.map(k => (
                        <td key={k} style={{ padding: '6px 8px' }}>
                          <input value={v.attributes[k] ?? ''} onChange={e => setVrows(vs => vs.map((x, j) => j === i ? { ...x, attributes: { ...x.attributes, [k]: e.target.value } } : x))} style={{ width: 110, padding: '3px 6px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 12 }} />
                        </td>
                      ))}
                      <td style={{ padding: '6px 8px' }}>
                        <input value={v.full_sku} onChange={e => setVrows(vs => vs.map((x, j) => j === i ? { ...x, full_sku: e.target.value } : x))} placeholder="SKU modelo" style={{ width: 160, padding: '3px 6px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.brand, fontSize: 11, fontFamily: theme.font.mono }} />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        <input type="number" step="0.01" value={v.price} onChange={e => setVrows(vs => vs.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} placeholder={p.msrp_usd != null ? String(p.msrp_usd) : '0'} style={{ width: 80, padding: '3px 6px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 12, textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        <input type="number" step="0.01" value={v.cost} onChange={e => setVrows(vs => vs.map((x, j) => j === i ? { ...x, cost: e.target.value } : x))} placeholder={cost.supplier_cost || '0'} style={{ width: 80, padding: '3px 6px', border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text, fontSize: 12, textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: theme.textMuted }}>{v.id ? (variantInv[v.id] ?? 0) : '—'}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        <button type="button" onClick={() => setVrows(vs => vs.filter((_, j) => j !== i))} style={{ background: 'transparent', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: 15 }}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 10 }}>
            <button type="button" onClick={() => setVrows(vs => [...vs, { keep: true, code: '', full_sku: '', attributes: Object.fromEntries(varyKeys.map(k => [k, ''])), price: p.msrp_usd != null ? String(p.msrp_usd) : '', cost: cost.supplier_cost || '' }])} style={{ background: 'transparent', border: `1px dashed ${theme.border}`, color: theme.brand, padding: '7px 12px', fontSize: 12, cursor: 'pointer', fontFamily: theme.font.sans }}>+ Agregar variante</button>
            <div style={{ flex: 1 }} />
            <button type="button" onClick={() => setVrows(vs => vs.map(x => ({ ...x, full_sku: [p.sku.trim(), (x.code || '').trim(), vsuffix.trim()].filter(Boolean).join('-') })))} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }} title="Re-aplica prefijo + código + sufijo a todos los SKU">Aplicar SKU a todas</button>
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>El prefijo/sufijo se define arriba (junto a Generar). El SKU modelo es editable por variante. Las variantes marcadas se guardan al dar {curMode === 'create' ? '“Crear producto”' : '“Guardar cambios”'}.</div>
        </div>

        {err && (
          <div style={{ color: '#B91C1C', fontSize: 13, marginTop: 12, padding: 10, background: 'rgba(220,38,38,0.05)', border: '1px solid #B91C1C' }}>
            {err}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{
            background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand,
            padding: '10px 18px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.4, cursor: 'pointer', fontFamily: theme.font.sans,
          }}>Cancelar</button>
          <button type="submit" disabled={saving} style={{
            ...btnPrimary,
            opacity: saving ? 0.7 : 1,
            cursor: saving ? 'wait' : 'pointer',
          }}>
            {saving ? 'Guardando…' : curMode === 'create' ? 'Crear producto' : 'Guardar cambios'}
          </button>
        </div>
      </form>

      {/* Variantes y fotos — solo en modo edit */}
      {curMode === 'edit' && curId && (
        <VariantsAndPhotos productId={curId} />
      )}
    </ProdModal>
  );
}

// ============ Variantes + fotos por variante ============
type Variant = {
  id: string;
  product_id: string;
  sku_suffix: string | null;
  full_sku: string | null;
  attributes: any;
  finish: string | null;
  color_temperature_k: number | null;
  wattage_w: number | null;
  primary_image_url: string | null;
};

function VariantsAndPhotos({ productId }: { productId: string }) {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [productImages, setProductImages] = useState<{ url: string; is_thumbnail: boolean; kind?: string; sort_order?: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [productSku, setProductSku] = useState<string>('');

  const load = async () => {
    setLoading(true);
    const [varsRes, prodRes] = await Promise.all([
      supabase.from('product_variants').select('id, product_id, sku_suffix, full_sku, attributes, finish, color_temperature_k, wattage_w, primary_image_url')
        .eq('product_id', productId).order('position'),
      supabase.from('products').select('sku, images').eq('id', productId).maybeSingle(),
    ]);
    setVariants((varsRes.data as Variant[]) ?? []);
    setProductImages(((prodRes.data as any)?.images as any[]) ?? []);
    setProductSku((prodRes.data as any)?.sku ?? '');
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [productId]);

  // Upload foto a Storage
  const uploadFile = async (file: File, kind: 'product' | 'variant', variantId?: string, photoKind: string = 'producto') => {
    const ext = file.name.split('.').pop() ?? 'jpg';
    const ts = Date.now();
    const target = kind === 'product'
      ? `${productSku}/${ts}.${ext}`
      : `${productSku}/variants/${variantId}-${ts}.${ext}`;

    setUploadingId(variantId ?? ('product-' + photoKind));
    const { error: upErr } = await supabase.storage.from('product-images').upload(target, file, { upsert: true });
    if (upErr) { alert(`Error subiendo: ${upErr.message}`); setUploadingId(null); return; }

    const { data: pub } = supabase.storage.from('product-images').getPublicUrl(target);
    const publicUrl = pub.publicUrl;

    if (kind === 'product') {
      const newImgs = [...productImages, { url: publicUrl, is_thumbnail: productImages.length === 0, sort_order: productImages.length, kind: photoKind }];
      await supabase.from('products').update({ images: newImgs }).eq('id', productId);
    } else if (variantId) {
      await supabase.from('product_variants').update({ primary_image_url: publicUrl }).eq('id', variantId);
    }
    setUploadingId(null);
    load();
  };

  const setThumbnail = async (url: string) => {
    const updated = productImages.map(i => ({ ...i, is_thumbnail: i.url === url }));
    await supabase.from('products').update({ images: updated }).eq('id', productId);
    load();
  };

  const deleteProductImage = async (url: string) => {
    if (!confirm('¿Borrar esta foto del producto?')) return;
    const updated = productImages.filter(i => i.url !== url);
    await supabase.from('products').update({ images: updated }).eq('id', productId);
    load();
  };

  const changeKind = async (url: string, newKind: string) => {
    const updated = productImages.map(i => i.url === url ? { ...i, kind: newKind } : i);
    await supabase.from('products').update({ images: updated }).eq('id', productId);
    load();
  };

  if (loading) return <div style={{ padding: 24, color: theme.textMuted }}>Cargando fotos…</div>;

  return (
    <div style={{ marginTop: 32, paddingTop: 24, borderTop: `2px solid ${theme.border}` }}>
      <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700, marginBottom: 4 }}>
        Fotos del producto
      </div>
      <div style={{ fontSize: 12, color: theme.textMuted, margin: '0 0 16px' }}>
        {productImages.length} fotos · organizadas por tipo. Súbelas en la sección correcta o reclasifícalas con el selector.
      </div>

      {([
        { key: 'producto',   label: 'Principales', match: (k?: string) => !k || k === 'producto' || k === 'foto' },
        { key: 'variante',   label: 'Variantes',   match: (k?: string) => k === 'variante' },
        { key: 'diagrama',   label: 'Diagramas',   match: (k?: string) => k === 'diagrama' },
        { key: 'aplicacion', label: 'Aplicación',  match: (k?: string) => k === 'aplicacion' || k === 'ambiance' || k === 'aplicativo' },
      ] as { key: string; label: string; match: (k?: string) => boolean }[]).map(g => {
        const imgs = productImages.filter(im => g.match(im.kind));
        return (
          <div key={g.key} style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700, marginBottom: 8 }}>
              {g.label} ({imgs.length})
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
              {imgs.map((img, i) => (
                <div key={i} style={{
                  position: 'relative', aspectRatio: '1', background: theme.bgImage,
                  border: img.is_thumbnail ? `2px solid ${theme.accent}` : `1px solid ${theme.border}`,
                  overflow: 'hidden',
                }}>
                  <img src={thumbUrl(img.url, 240)} alt="" loading="lazy" decoding="async"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
                  {img.is_thumbnail && (
                    <span style={{ position: 'absolute', top: 4, left: 4, background: theme.accent, color: theme.brand, fontSize: 9, fontWeight: 700, padding: '2px 6px', textTransform: 'uppercase' }}>★ Principal</span>
                  )}
                  <select value={g.key} onChange={e => changeKind(img.url, e.target.value)} title="Tipo de foto"
                    style={{ position: 'absolute', top: 4, right: 4, fontSize: 9, border: 'none', background: 'rgba(255,255,255,0.9)', color: theme.brand, fontFamily: theme.font.sans, cursor: 'pointer', padding: '1px 2px' }}>
                    <option value="producto">Principal</option>
                    <option value="variante">Variante</option>
                    <option value="diagrama">Diagrama</option>
                    <option value="aplicacion">Aplicación</option>
                  </select>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', padding: 4, background: 'rgba(255,255,255,0.85)' }}>
                    {!img.is_thumbnail && (
                      <button onClick={() => setThumbnail(img.url)} style={miniBtn} title="Hacer principal">★</button>
                    )}
                    <button onClick={() => deleteProductImage(img.url)} style={{ ...miniBtn, color: '#B91C1C', marginLeft: 'auto' }} title="Borrar">×</button>
                  </div>
                </div>
              ))}
              <FileDrop
                uploading={uploadingId === 'product-' + g.key}
                onPick={(file) => uploadFile(file, 'product', undefined, g.key)}
              />
            </div>
          </div>
        );
      })}

      {/* Variantes */}
      {variants.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700, marginTop: 24, marginBottom: 4 }}>
            Variantes
          </div>
          <h3 style={{ fontFamily: theme.font.head, fontSize: 18, fontWeight: 400, color: theme.brand, margin: '0 0 16px' }}>
            {variants.length} variantes · foto por color/acabado
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {variants.map(v => (
              <div key={v.id} style={{ border: `1px solid ${theme.border}`, padding: 10 }}>
                <div style={{
                  aspectRatio: '1', background: theme.bgImage,
                  position: 'relative', overflow: 'hidden',
                  marginBottom: 8,
                }}>
                  {v.primary_image_url && (
                    <img
                      src={thumbUrl(v.primary_image_url, 240)}
                      alt={v.finish ?? ''}
                      loading="lazy"
                      decoding="async"
                      style={{
                        position: 'absolute', top: 0, left: 0,
                        width: '100%', height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                </div>
                <div style={{ fontSize: 10, color: theme.textMuted, fontFamily: theme.font.mono }}>
                  {v.sku_suffix ?? v.full_sku ?? ''}
                </div>
                <div style={{ fontSize: 12, color: theme.brand, fontWeight: 600, marginTop: 2 }}>
                  {v.finish ?? Object.values(v.attributes ?? {}).filter(Boolean).join(' · ') ?? '—'}
                </div>
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
                  {[v.color_temperature_k && `${v.color_temperature_k}K`, v.wattage_w && `${v.wattage_w}W`].filter(Boolean).join(' · ')}
                </div>
                <FilePicker
                  uploading={uploadingId === v.id}
                  hasImage={!!v.primary_image_url}
                  onPick={(file) => uploadFile(file, 'variant', v.id)}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FileDrop({ uploading, onPick }: { uploading: boolean; onPick: (f: File) => void }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      disabled={uploading}
      style={{
        aspectRatio: '1', border: `2px dashed ${theme.border}`,
        background: theme.bgSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', cursor: uploading ? 'wait' : 'pointer',
        color: theme.textMuted, fontFamily: theme.font.sans, fontSize: 11, padding: 8,
      }}
    >
      <div style={{ fontSize: 24 }}>{uploading ? '⏳' : '+'}</div>
      <div>{uploading ? 'Subiendo…' : 'Subir foto'}</div>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={e => {
        const f = e.target.files?.[0]; if (f) onPick(f);
      }} />
    </button>
  );
}

function FilePicker({ uploading, hasImage, onPick }: { uploading: boolean; hasImage: boolean; onPick: (f: File) => void }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        style={{
          width: '100%', marginTop: 8,
          background: hasImage ? 'transparent' : theme.brand,
          color: hasImage ? theme.brand : theme.textOnBrand,
          border: hasImage ? `1px solid ${theme.border}` : 'none',
          padding: '6px 10px', fontSize: 10, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.4,
          cursor: uploading ? 'wait' : 'pointer', fontFamily: theme.font.sans,
        }}
      >
        {uploading ? 'Subiendo…' : hasImage ? 'Cambiar foto' : '+ Subir foto'}
      </button>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={e => {
        const f = e.target.files?.[0]; if (f) onPick(f);
      }} />
    </>
  );
}

const miniBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', color: theme.brand,
  cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: '2px 6px',
};

function ProdModal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg, border: `1px solid ${theme.border}`,
        padding: 32, width: '100%', maxWidth: 720, maxHeight: '92vh', overflowY: 'auto',
        position: 'relative',
      }}>
        <button type="button" onClick={onClose} aria-label="Cerrar" title="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>
        {children}
      </div>
    </div>
  );
}

function PField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={{
        fontSize: 11, color: theme.textMuted, marginBottom: 6,
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
      }}>{label}</div>
      {children}
    </label>
  );
}

function PRow({ children }: { children: React.ReactNode }) {
  const cols = Array.isArray(children) ? children.length : 1;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  padding: '10px 12px',
  color: theme.text,
  fontSize: 13,
  fontFamily: theme.font.sans,
  outline: 'none',
  boxSizing: 'border-box',
};

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left',
      padding: '12px 14px',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: theme.textMuted,
      borderBottom: `1px solid ${theme.border}`,
      ...style,
    }}>{children}</th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '10px 14px', verticalAlign: 'top', ...style }}>{children}</td>;
}

function Tag({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      padding: '2px 8px',
      color,
      border: `1px solid ${color}`,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}>{children}</span>
  );
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : theme.bg,
      color: active ? theme.textOnBrand : theme.brand,
      border: `1px solid ${active ? theme.brand : theme.border}`,
      padding: '8px 12px',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>{children}</button>
  );
}

function LinePill({ active, color, onClick, children }: { active: boolean; color: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? color : 'transparent',
      color: active ? '#fff' : theme.brand,
      border: `1.5px solid ${color}`,
      padding: '6px 10px',
      fontSize: 11,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>{children}</button>
  );
}

const btnPrimary: React.CSSProperties = {
  background: theme.accent,
  color: theme.brand,
  border: 'none',
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: theme.font.sans,
};

function fileToB64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const str = String(r.result); resolve(str.split(',')[1] ?? str); };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function ExtractModal({ onClose, onApply }: { onClose: () => void; onApply: (r: any) => void }) {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setBusy(true); setErr(null);
    try {
      const body: any = {};
      if (file) {
        const b64 = await fileToB64(file);
        if (file.type === 'application/pdf') body.pdf_base64 = b64;
        else { body.image_base64 = b64; body.media_type = file.type || 'image/jpeg'; }
      }
      if (text.trim()) body.text = text.trim();
      if (!body.pdf_base64 && !body.image_base64 && !body.text) { setErr('Sube un archivo o pega el texto de la ficha'); setBusy(false); return; }
      const { data, error } = await supabase.functions.invoke('extract-product-specs', { body });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      onApply((data as any).result);
      onClose();
    } catch (e: any) { setErr(e.message ?? 'Error al extraer'); } finally { setBusy(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: theme.bg, border: `1px solid ${theme.border}`, padding: 28, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
        <button type="button" onClick={onClose} aria-label="Cerrar" title="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>
        <h2 style={{ fontFamily: theme.font.head, fontSize: 20, fontWeight: 400, color: theme.brand, margin: '0 0 8px' }}>Extraer de ficha del proveedor</h2>
        <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 14, lineHeight: 1.5 }}>
          Sube la ficha (PDF o imagen) o pega el texto. La IA extrae y traduce las características al español y prellena el formulario. <strong>Revisa antes de guardar.</strong>
        </div>
        <div style={{ marginBottom: 12 }}>
          <input type="file" accept=".pdf,image/*" onChange={e => setFile(e.target.files?.[0] ?? null)} style={{ fontSize: 13, color: theme.text }} />
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={6} placeholder="…o pega aquí el texto de la ficha (útil para Excel)"
          style={{ width: '100%', background: theme.bg, border: `1px solid ${theme.border}`, padding: 10, color: theme.text, fontSize: 12, fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
        {err && <div style={{ color: '#B91C1C', fontSize: 13, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '9px 16px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', cursor: 'pointer', fontFamily: theme.font.sans }}>Cancelar</button>
          <button type="button" onClick={run} disabled={busy} style={{ background: theme.accent, color: theme.brand, border: 'none', padding: '9px 16px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', cursor: busy ? 'wait' : 'pointer', fontFamily: theme.font.sans, opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Extrayendo…' : 'Extraer con IA'}
          </button>
        </div>
      </div>
    </div>
  );
}
